import { User } from '../@types/user'
import { PropertyLocation } from '../@types/location'
import {
  BackendReceiveSolarLeadRequest,
  OwnerBackendReceiveLeadRequest,
  ReceiveLeadRequestRealEstateType,
} from '../@types/ownerBackendReceiveLeadRequest'
import { Lead, LeadSource, OptionalLeadSource } from '../@types/lead'
import { Resource } from '../@types/resource'
import { Valuation } from '../@types/valuation'
import { Status } from '../@types/status'
import { RealEstateType } from '../@types/realEstateType'
import { EnrichLeadRequest } from '../@types/enrichLeadRequest'
import { sendErrorLog } from './logClient'
import { ErrorLogSource } from '../@types/errorLog'
import { Intent } from '../@types/intent'
import { IntegrationType } from '../@types/config'
import { Iv24TrackingData } from '../store/immoverkauf24/trackingSlice'
import { LocationState } from '../store/location/locationSlice'
import { Address } from '../@types/address'

export interface LeadResponse {
  rawLeadId: string
  ssoId: number
  errorMessages?: []
  tealiumTrackingMessage?: string
}

export interface SolarLeadResponse {
  ssoId: number
  errorMessages?: []
}

export interface ReceiveLeadRequest {
  user: User
  lead: Lead
  location: LocationState
  valuation: Resource<Valuation>
  coopId?: string | undefined
  integrationType?: IntegrationType
  iv24TrackingData: Iv24TrackingData
  address?: Resource<Address> | undefined
}

export interface ReceiveSolarLeadRequest {
  user: User
  lead: Lead
  location: Resource<PropertyLocation>
  coopId?: string | undefined
}

export const enrichLead = async (request: EnrichLeadRequest): Promise<void> => {
  return fetch(`${process.env.RLE_BACKEND_URL}/v2/receive-leads`, {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`${response.status}`)
      return Promise.resolve()
    })
    .catch((error) => {
      const errorMessage = `Error while enriching lead, message: ${error.message}`
      sendErrorLog({
        message: errorMessage,
        source: ErrorLogSource.ENRICH_LEAD,
      })
      throw new Error(errorMessage)
    })
}

export const submitLead = async (leadRequest: ReceiveLeadRequest): Promise<LeadResponse> =>
  fetch(`${process.env.RLE_BACKEND_URL}/receive-leads`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mapReceiveLeadRequestToReceiveLeadRequest(leadRequest)),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`${response.status}`)
      return response.json()
    })
    .catch((error) => {
      const errorMessage = `Error while sending lead, message: ${error.message}`
      sendErrorLog({
        message: errorMessage,
        source: ErrorLogSource.RECEIVE_LEAD,
      })
      throw new Error(errorMessage)
    })

export const getLeadSource = (integrationType: IntegrationType | undefined, lead: Lead): string => {
  const leadSource = window.IS24?.RLE?.optionalLeadSource
  if (integrationType === IntegrationType.EXTERNAL) {
    if (lead.leadSource === LeadSource.REGULAR) {
      return LeadSource.COOPERATION_EXTERNAL
    } else if (lead.leadSource === LeadSource.IBW) {
      return LeadSource.IBW_COOPERATION_EXTERNAL
    } else if (leadSource === OptionalLeadSource.RLE_COOPERATION) {
      return LeadSource.COOPERATION
    } else if (leadSource === OptionalLeadSource.IBW_COOPERATION) {
      return LeadSource.IBW_COOPERATION
    }
  } else if (integrationType === IntegrationType.IMMOVERKAUF24) {
    return LeadSource.IV24_WEBSEITE_D
  }
  if (leadSource === OptionalLeadSource.ATLAS) {
    return LeadSource.ATLAS
  }
  return lead.leadSource === LeadSource.REGULAR ? LeadSource.REGULAR : LeadSource.IBW
}

export const mapReceiveLeadRequestToReceiveLeadRequest = (
  leadRequest: ReceiveLeadRequest
): OwnerBackendReceiveLeadRequest => {
  const { lead, user, location, valuation, coopId, integrationType, iv24TrackingData, address } = leadRequest
  return {
    salutation: user.salutation,
    acceptance: user.dataProtectionAccepted,
    firstname: user.firstName,
    lastname: user.lastName,
    cellphonenumber: user.phoneNumber,
    email: user.email,
    postcode: location.data ? location.data.postcode : '',
    gacId: location.data && location.data.gacId ? location.data.gacId : '',
    address: address && address.data ? `${address.data.street} ${address.data.houseNumber}` : null,
    leadSource: getLeadSource(integrationType, lead),
    propertyOfferOptIn: lead.intent == Intent.INDEPENDENT_WITH_OFFER_SALE,
    userIntent: lead.intent === Intent.INDEPENDENT_WITH_OFFER_SALE ? Intent.INDEPENDENT_SALE : lead.intent,
    city: location.data && location.data.city ? location.data.city : '',
    livingspace: lead.livingSpace,
    propertysize: lead.plotArea ? lead.plotArea : null,
    numberofrooms: lead.rooms,
    ownership: lead.ownership,
    specialization: lead.realEstateType
      ? ReceiveLeadRequestRealEstateType[lead.realEstateType]
      : ReceiveLeadRequestRealEstateType.DEFAULT,
    saletimehorizon: lead.timeHorizon,
    duplicateUser: lead.duplicateUser,
    geotype: 'postcode',
    valuation:
      valuation.status == Status.SUCCESS && valuation.data
        ? `${valuation.data.range.min}-${valuation.data.range.max}`
        : null,
    propertydevelopment: lead.landDevelopmentPotential,
    propertydeveloped: lead.landDevelopmentStatus,
    propertycut: lead.landLayout,
    commercialPropertyType: lead.commercialRealEstateType,
    landSizeEnabled: lead.realEstateType === RealEstateType.COMMERCIAL && lead.plotArea ? true : undefined,
    cooperationId: coopId,
    integrationType: integrationType,
    locationRange: location.locationRange,

    //IV24
    businessArea: iv24TrackingData.businessArea,
    iv24Intention: iv24TrackingData.iv24Intention,
    formType: iv24TrackingData.formType,
    formUrl: iv24TrackingData.formUrl,
    formPage: iv24TrackingData.formPage,
    adGroup: iv24TrackingData.adGroup,
    adKeyword: iv24TrackingData.adKeyword,
    adPlacement: iv24TrackingData.adPlacement,
    adCampaign: iv24TrackingData.adCampaign,
    adChannel: iv24TrackingData.adChannel,
    adDevice: iv24TrackingData.adDevice,
    kwMatchType: iv24TrackingData.kwMatchType,
    clickId: iv24TrackingData.clickId,
    qcTransactionId: iv24TrackingData.qcTransactionId,

    // Extended buyer leads
    affordability: lead.affordability,
    extendedBuyerLead: lead.extendedBuyerLead,
    equity: lead.equity,
    income: lead.income,
    employment: lead.employment,
    furtherRealEstateInformation: lead.furtherRealEstateInformation,

    isConsultationAccepted: lead.isConsultationAccepted,
  }
}

export const submitSolarLead = async (leadRequest: ReceiveSolarLeadRequest): Promise<SolarLeadResponse> =>
  fetch(`${process.env.SOLAR_BACKEND_URL}/receive-leads`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(mapReceiveSolarLeadRequestToReceiveSolarLeadRequest(leadRequest)),
  })
    .then((response) => {
      if (!response.ok) throw new Error(`${response.status}`)
      return response.json()
    })
    .catch((error) => {
      const errorMessage = `Error while sending solar lead, message: ${error.message}`
      sendErrorLog({
        message: errorMessage,
        source: ErrorLogSource.RECEIVE_SOLAR_LEAD,
      })
      throw new Error(errorMessage)
    })

export const mapReceiveSolarLeadRequestToReceiveSolarLeadRequest = (
  leadRequest: ReceiveSolarLeadRequest
): BackendReceiveSolarLeadRequest => {
  const { lead, user, location, coopId } = leadRequest
  return {
    acceptance: user.dataProtectionAccepted,
    placementType: lead.placementType,
    intent: lead.solarIntent,
    saleTimeHorizon: lead.timeHorizon,
    postcode: location.data ? location.data.postcode : '',
    phoneNumber: user.phoneNumber,
    salutation: user.salutation,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    cooperationId: coopId,
  }
}
